import { debounce, isFunction } from '@agilepoint/utils-common';
import * as React from 'react';
import { Transition, TransitionRenderFn } from 'react-spring';
import { CarouselItemWrapper } from './parts/carousel-item-wrapper';
import { IRollingCardCarouselProps } from './types';
import { IItemProps, ItemsWrapper } from '../../common/carousel-item';
import { CarouselWrapper } from '../../common/wrapper';
import { CarouselContext } from '../../common/carousel-context';
import { ListIterator } from '../../../../utils';

interface ICarouselState {
    activeKey?: React.Key;
}

export class RollingCardCarousel extends React.PureComponent<IRollingCardCarouselProps, ICarouselState> {
    private containerRef = React.createRef<HTMLDivElement>();
    private itemsMap = new Map<React.Key, IItemProps>();
    private itemsIterator = new ListIterator<React.Key>([], { loop: true });

    static getDerivedStateFromProps(props: IRollingCardCarouselProps) {
        const { selectedId } = props;
        if (selectedId !== undefined) {
            return {
                activeKey: selectedId
            };
        }
        return null;
    }

    private updateComponent = debounce(() => {
        const { activeKey } = this.state;
        const newKeys = Array.from(this.itemsMap.keys());
        this.itemsIterator.updateList(newKeys);
        if (activeKey === undefined) {
            this.itemsIterator.next();
            return;
        }
        this.itemsIterator.find(key => key === activeKey);
        this.forceUpdate();
    }, 100, this);

    constructor(props: IRollingCardCarouselProps) {
        super(props);
        const { defaultSelectedId, selectedId } = this.props;
        this.state = {
            activeKey: selectedId ?? defaultSelectedId
        };
    }

    componentDidMount() {
        this.itemsIterator.subscribeToOnChange(this._onChange);
    }

    componentWillUnmount() {
        this.itemsIterator.unsubscribeToOnChange(this._onChange);
    }

    componentDidUpdate() {
        const { selectedId } = this.props;
        if (selectedId !== undefined && this.itemsIterator.getValue() !== selectedId) {
            this.itemsIterator.find(key => key === selectedId);
        }
    }

    render() {
        return (
            <CarouselWrapper ref={this.containerRef}>
                {this.onRenderItems()}
                {this.renderItemsToBeDisplayed()}
            </CarouselWrapper>
        );
    }

    private addItem = (props: IItemProps) => {
        const { id } = props;
        this.itemsMap.set(id!, props);
        this.updateComponent();
    }

    private removeItem = (key: React.Key) => {
        this.itemsMap.delete(key!);
        this.updateComponent();
    }

    private onRenderItems() {
        const { children } = this.props;
        return (
            <CarouselContext.Provider value={{ addItem: this.addItem, removeItem: this.removeItem }}>
                {children}
            </CarouselContext.Provider>
        );
    }

    private renderItemsToBeDisplayed() {
        const { animationDuration = 150 } = this.props;
        const itemsToRender = this.getItemsToRender();
        return (
            <ItemsWrapper alignItems="center">
                <Transition items={itemsToRender} config={{ duration: animationDuration }}
                    initial={{ scale: 0, bottom: 0, opacity: 1 }}
                    enter={this.getAnimationStyle}
                    update={this.getAnimationStyle}
                    leave={{ scale: 0, opacity: 0, transformOrigin: '50% 100%', config: { duration: 200 } }}>
                    {this.onItemRender}
                </Transition>
            </ItemsWrapper >
        );
    }

    private onItemRender: TransitionRenderFn = (style, key, tr, idx) => {
        const { offset = 35 } = this.props;
        const itemProps = this.itemsMap.get(key) ?? {};
        const bufferCount = this.getItemsToRenderCount() - 1;
        return (
            <CarouselItemWrapper
                style={style}
                itemId={key}
                key={key}
                maxHeight={`calc(100% - ${offset * bufferCount}px)`}
                index={bufferCount - idx}
                onClick={this._onClick}
                {...itemProps} />
        );
    }

    private _onClick = (id: React.Key) => {
        this.itemsIterator.find(key => key === id);
    }

    private _onChange = (activeKey: React.Key) => {
        const { selectedId, onChange } = this.props;
        const { activeKey: prevActiveKey } = this.state;
        if (selectedId === undefined) {
            this.setState({
                activeKey
            });
        }
        if (isFunction(onChange) && prevActiveKey !== activeKey) {
            onChange(activeKey);
        }
    }

    private getAnimationStyle = (item: React.Key, idx: number) => {
        const { offset = 35 } = this.props;
        const bufferCount = this.getItemsToRenderCount() - 1;
        return { top: (bufferCount - idx) * offset, scale: (1 - (idx * 0.15)), opacity: 1 };
    }

    private getItemsToRenderCount = () => {
        const { bufferCount = 1 } = this.props;
        const allKeys = Array.from(this.itemsMap.keys());
        return Math.min(Math.max(bufferCount, 1), allKeys.length - 1) + 1;
    }

    private getItemsToRender() {
        const { activeKey } = this.state;
        const itemsToRender: Array<React.Key> = [];
        const allKeys = Array.from(this.itemsMap.keys());
        const currentSelectionIndex = activeKey === undefined ? 0 : allKeys.indexOf(activeKey);
        const itemsToRenderCount = this.getItemsToRenderCount();

        for (let i = 0; i < itemsToRenderCount; i++) {
            let newIndex = ((currentSelectionIndex + i) % allKeys.length);
            if (newIndex < 0) {
                newIndex += allKeys.length;
            }
            const key = allKeys[newIndex];
            if (key === undefined) {
                continue;
            }
            itemsToRender.push(key);
        }

        return itemsToRender;
    }
}
